/**
 * The general ledger, and the drill-down that ends at the original document.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THE CHAIN THIS SCREEN COMPLETES.
 *
 *   report figure → account → transaction → journal → invoice or order →
 *   the scanned document the figures were taken from
 *
 * A journal here shows its lines, the sub-ledger document that produced it, the
 * documents attached to that, and every audit event about either. Somebody
 * asking "why is this number what it is?" gets an answer without leaving the
 * system, and without anybody having to look in a filing cabinet.
 *
 * A posted journal cannot be altered. Not by this screen, not by the API, and
 * not by direct SQL — a database trigger refuses it. Correcting a posted
 * journal means reversing it, and the reversal keeps a pointer to what it
 * reversed and why.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTerms, type Terminology } from '../lib/tenant';
import { date, dateTime, humanise, money, number, statusLabel, statusTone, todayIso } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  TextArea,
  TextInput,
} from '../components/ui';

interface JournalRow {
  id: string;
  journal_no: string;
  document_type_code: string;
  effective_date: string;
  description: string;
  reference: string | null;
  status: string;
  source_module: string | null;
  total_debit_minor: number;
  period_name: string;
  created_by_name: string | null;
  posted_by_name: string | null;
  line_count: number;
}

/**
 * Plain-English names for the two-letter document type codes.
 *
 * "DP", "BP" and "PJ" mean something to the people who built the chart of
 * document types and to nobody else, and the tenant's own reference data
 * already carries the name for every one of them.
 */
function useDocumentTypeName(): (code: string | null | undefined) => string {
  const types = useAsync(
    () => api.get<{ documentTypes: Array<{ code: string; name: string }> }>('/reference/document-types'),
    [],
  );
  const byCode = useMemo(() => {
    const map: Record<string, string> = {};
    for (const type of types.data?.documentTypes ?? []) map[type.code] = type.name;
    return map;
  }, [types.data]);
  // Until the lookup answers, the code itself stands in: a momentary "PI" is
  // honest, and an empty cell is not.
  return (code) => (code ? byCode[code] ?? code : '—');
}

export function LedgerPage() {
  const t = useTerms();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const documentTypeName = useDocumentTypeName();
  const list = useAsync(
    () =>
      api.get<{ journals: JournalRow[] }>(
        `/ledger/journals?${new URLSearchParams({ ...(search ? { search } : {}), ...(status ? { status } : {}) })}`,
      ),
    [search, status],
  );

  return (
    <>
      <PageHeader
        title="General ledger"
        subtitle="Every posting, from every module. A posted journal is immutable — the database refuses to change one."
      />

      <Card
        title="Journals"
        flush
        actions={
          <div className="row" style={{ gap: '0.5rem' }}>
            <label>
              <span className="visually-hidden">Search journals</span>
              <input
                type="search"
                placeholder="Number, reference or description"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </label>
            <label className="row" style={{ gap: '0.4rem' }}>
              <span className="smaller muted">Status</span>
              <select value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="posted">Posted</option>
                <option value="reversed">Reversed</option>
              </select>
            </label>
          </div>
        }
      >
        <AsyncPanel state={list}>
          {(data) => (
            <DataTable
              caption={`Journals in this ${lower(t.entity)}`}
              rows={data.journals}
              rowKey={(j) => j.id}
              emptyMessage="No journal matches those filters."
              pageSize={50}
              columns={[
                {
                  key: 'no',
                  header: 'Journal',
                  width: '12rem',
                  sortValue: (j) => j.journal_no,
                  render: (j) => (
                    <>
                      <Link to={`/ledger/journals/${j.id}`}>
                        <span className="mono nowrap">{j.journal_no}</span>
                      </Link>
                      {/*
                        Bounded so a long document-type name is clipped with an
                        ellipsis rather than widening the column: an unbounded
                        nowrap string sets the column's minimum width.
                      */}
                      <div
                        className="smaller muted truncate"
                        style={{ maxWidth: '10.5rem' }}
                        title={j.document_type_code}
                      >
                        {documentTypeName(j.document_type_code)}
                      </div>
                    </>
                  ),
                },
                {
                  key: 'desc',
                  header: 'Description',
                  sortValue: (j) => j.description,
                  render: (j) => (
                    <>
                      {/*
                        The bound is what makes `truncate` work. Without a width
                        limit the cell grows to the longest description — around
                        130 characters here — and pushes the money and status
                        columns off the side of the table.
                      */}
                      <div className="truncate" style={{ maxWidth: '22rem' }} title={j.description}>
                        {j.description}
                      </div>
                      {j.reference ? <div className="smaller muted mono">{j.reference}</div> : null}
                    </>
                  ),
                },
                {
                  key: 'date',
                  header: 'Effective',
                  sortValue: (j) => j.effective_date,
                  render: (j) => <span className="smaller nowrap">{date(j.effective_date)}</span>,
                },
                {
                  key: 'period',
                  header: 'Period',
                  sortValue: (j) => j.effective_date,
                  render: (j) => <span className="smaller nowrap">{j.period_name}</span>,
                },
                {
                  key: 'source',
                  header: 'From',
                  sortValue: (j) => j.source_module ?? 'general_ledger',
                  render: (j) => <span className="smaller muted">{statusLabel(j.source_module ?? 'general_ledger')}</span>,
                },
                {
                  key: 'lines',
                  header: 'Lines',
                  numeric: true,
                  sortValue: (j) => j.line_count,
                  render: (j) => j.line_count,
                },
                {
                  key: 'value',
                  header: 'Value',
                  numeric: true,
                  sortValue: (j) => j.total_debit_minor,
                  render: (j) => <Money minor={j.total_debit_minor} />,
                },
                {
                  key: 'status',
                  header: 'Status',
                  sortValue: (j) => j.status,
                  render: (j) => <Badge tone={statusTone(j.status)}>{statusLabel(j.status)}</Badge>,
                },
              ]}
            />
          )}
        </AsyncPanel>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Journal detail — the end of the drill-down
 * ------------------------------------------------------------------ */

interface JournalTrail {
  journal: Record<string, unknown> & {
    id: string;
    journal_no: string;
    description: string;
    reference: string | null;
    status: string;
    effective_date: string;
    posting_date: string | null;
    period_name: string;
    financial_year_code: string;
    document_type_code: string;
    source_module: string | null;
    source_table: string | null;
    total_debit_minor: number;
    total_credit_minor: number;
    created_by_name: string | null;
    posted_by_name: string | null;
    approved_by_name: string | null;
    reversal_reason: string | null;
    reversed_by_journal_no: string | null;
    reversed_by_journal_id: string | null;
    reverses_journal_no: string | null;
    reverses_journal_id: string | null;
  };
  lines: Array<
    Record<string, unknown> & {
      id: string;
      line_no: number;
      description: string | null;
      debit_minor: number;
      credit_minor: number;
      account_code: string;
      account_name: string;
      cost_centre_code: string | null;
      cost_centre_name: string | null;
      project_code: string | null;
      project_name: string | null;
    }
  >;
  source: { table: string | null; id: string | null; document: Record<string, unknown> | null };
  documents: Array<{ id: string; title: string | null; filename: string; document_type?: string; byte_size: number; uploaded_at: string; uploaded_by_name?: string | null; content_sha256?: string }>;
  auditEvents: Array<{ id: string; event_type: string; module: string | null; summary: string; occurred_at: string; actor_name: string | null; actor_label: string }>;
}

/**
 * Sub-ledger records that have a page of their own.
 *
 * Nothing is listed here that the router cannot actually open. A sales invoice
 * and a goods receipt are both absent for that reason — the app has a list for
 * each but no record page — and a link to a route that does not exist does not
 * fail visibly, it silently returns the viewer to the dashboard, which looks
 * like the application throwing them out of the record. A goods receipt instead
 * leads on to the order it was received against; see `orderPointer` below.
 */
const SOURCE_ROUTES: Record<string, string> = {
  purchase_invoice: '/invoices',
  purchase_order: '/orders',
  payment_batch: '/payments',
};

/** Payment methods are acronyms as often as words, so they are named rather than humanised. */
const PAYMENT_METHODS: Record<string, string> = {
  bacs: 'BACS',
  chaps: 'CHAPS',
  faster_payment: 'Faster payment',
  cheque: 'Cheque',
  direct_debit: 'Direct debit',
  card: 'Card',
};

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_LIKE = /^\d{4}-\d{2}-\d{2}(?:T|$)/;
const MACHINE_LIKE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The fields of a sub-ledger document.
 *
 * The API answers with a wrapper — { type, header, lines, … } — because the
 * lines and the match results travel with it. Everything a reader wants to see
 * is on the header, one level down; reading the wrapper itself yields the
 * object, not the invoice.
 */
function headerOf(document: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!document) return null;
  return isRecord(document.header) ? document.header : document;
}

function textField(header: Record<string, unknown> | null, key: string): string | null {
  const value = header?.[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The reference a reader would quote for this document: its own number. */
function documentReference(header: Record<string, unknown> | null): string | null {
  for (const key of ['invoice_no', 'grn_no', 'batch_no', 'receipt_no', 'asset_no', 'order_no']) {
    const value = textField(header, key);
    if (value) return value;
  }
  return null;
}

/**
 * The order a sub-ledger document was raised against, when it has one.
 *
 * Both a goods receipt and a matched purchase invoice point back at their
 * order, which is the next link in the chain this screen exists to show.
 */
function orderPointer(header: Record<string, unknown> | null): { id: string; reference: string | null } | null {
  const id = textField(header, 'purchase_order_id');
  return id ? { id, reference: textField(header, 'order_no') } : null;
}

/**
 * The business noun for a sub-ledger table.
 *
 * Taken from terminology rather than from the table name, so a tenant that
 * calls an order something else reads its own word here as well.
 */
function sourceNoun(table: string | null | undefined, t: Terminology): string {
  if (!table) return 'Source document';
  const nouns: Record<string, string> = {
    purchase_invoice: 'Purchase invoice',
    purchase_order: t.purchaseOrder,
    goods_receipt: 'Goods receipt',
    payment_batch: 'Payment run',
    sales_invoice: 'Sales invoice',
    customer_receipt: `${t.customer} receipt`,
    depreciation_run: 'Depreciation run',
    fixed_asset: 'Fixed asset',
  };
  return nouns[table] ?? statusLabel(table);
}

export function JournalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useSession();
  const t = useTerms();
  const documentTypeName = useDocumentTypeName();
  const detail = useAsync(() => api.get<JournalTrail>(`/ledger/journals/${id}`), [id]);
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState('');
  // Today, because the server reverses on the ORIGINAL journal's date when this
  // is left empty — and that period is usually closed by the time anybody wants
  // to correct it.
  const [reversalDate, setReversalDate] = useState(todayIso());

  const reverse = useMutation(async () =>
    api.post<{ journalNo: string }>(`/ledger/journals/${id}/reverse`, {
      reason,
      effectiveDate: reversalDate || undefined,
    }),
  );

  // Which document is being fetched, so only the button that was pressed says
  // so rather than every row in the table at once.
  const [opening, setOpening] = useState<string | null>(null);
  const openDocument = useMutation((documentId: string, filename: string) =>
    api.download(`/operations/documents/${documentId}/content`, filename),
  );

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const j = data.journal;
        const header = headerOf(data.source.document);
        const sourceRoute = j.source_table ? SOURCE_ROUTES[j.source_table] : undefined;
        const sourceReference = documentReference(header);
        const order = j.source_table === 'purchase_order' ? null : orderPointer(header);

        return (
          <>
            <PageHeader
              title={`Journal ${j.journal_no}`}
              subtitle={j.description}
              meta={
                <>
                  <Badge tone={statusTone(j.status)}>{statusLabel(j.status)}</Badge>
                  <span>
                    {j.period_name} · {j.financial_year_code}
                  </span>
                  <span>Effective {date(j.effective_date)}</span>
                  <span title={j.document_type_code}>{documentTypeName(j.document_type_code)}</span>
                </>
              }
              actions={
                j.status === 'posted' && !j.reversed_by_journal_id && can('gl.journal.reverse') ? (
                  <button type="button" className="btn btn--danger" onClick={() => setReversing(true)}>
                    Reverse this journal
                  </button>
                ) : null
              }
            />

            {/* The drill-down trail, shown as a trail. */}
            <nav className="drill-trail" aria-label="How this figure was reached">
              <Link to="/reports">Reports</Link>
              <span className="drill-trail__sep" aria-hidden="true">
                ›
              </span>
              <Link to="/ledger">General ledger</Link>
              <span className="drill-trail__sep" aria-hidden="true">
                ›
              </span>
              <span className="mono">{j.journal_no}</span>
              {j.source_table && data.source.document ? (
                <>
                  <span className="drill-trail__sep" aria-hidden="true">
                    ›
                  </span>
                  {sourceRoute && data.source.id ? (
                    <Link to={`${sourceRoute}/${data.source.id}`}>
                      {sourceNoun(j.source_table, t)}
                      {sourceReference ? <span className="mono"> {sourceReference}</span> : null}
                    </Link>
                  ) : (
                    <span>
                      {sourceNoun(j.source_table, t)}
                      {sourceReference ? <span className="mono"> {sourceReference}</span> : null}
                    </span>
                  )}
                </>
              ) : null}
              {order ? (
                <>
                  <span className="drill-trail__sep" aria-hidden="true">
                    ›
                  </span>
                  <Link to={`/orders/${order.id}`}>
                    {t.purchaseOrder}
                    {order.reference ? <span className="mono"> {order.reference}</span> : null}
                  </Link>
                </>
              ) : null}
              {data.documents.length > 0 ? (
                <>
                  <span className="drill-trail__sep" aria-hidden="true">
                    ›
                  </span>
                  <span>
                    {data.documents.length} original document{data.documents.length === 1 ? '' : 's'}
                  </span>
                </>
              ) : null}
            </nav>

            {j.reverses_journal_no ? (
              <Alert tone="info" title="This journal reverses another">
                <p>
                  It reverses{' '}
                  <Link to={`/ledger/journals/${j.reverses_journal_id}`}>
                    <span className="mono">{j.reverses_journal_no}</span>
                  </Link>
                  . {j.reversal_reason}
                </p>
              </Alert>
            ) : null}
            {j.reversed_by_journal_no ? (
              <Alert tone="warn" title="This journal has been reversed">
                <p>
                  Reversed by{' '}
                  <Link to={`/ledger/journals/${j.reversed_by_journal_id}`}>
                    <span className="mono">{j.reversed_by_journal_no}</span>
                  </Link>
                  . The original posting is still here, unchanged — that is what makes the trail complete.
                </p>
              </Alert>
            ) : null}

            <div className="grid grid--3">
              <div style={{ gridColumn: 'span 2' }} className="stack">
                <Card title="Lines" flush>
                  <DataTable
                    caption={`Lines of journal ${j.journal_no}`}
                    rows={data.lines}
                    rowKey={(l) => l.id}
                    columns={[
                      { key: 'no', header: '#', width: '3rem', render: (l) => l.line_no },
                      {
                        key: 'account',
                        header: 'Nominal code',
                        render: (l) => (
                          <>
                            <span className="mono">{l.account_code}</span> {l.account_name}
                            {l.description ? <div className="smaller muted">{l.description}</div> : null}
                          </>
                        ),
                      },
                      {
                        key: 'coding',
                        header: t.costCentre,
                        render: (l) =>
                          l.cost_centre_code ? (
                            <>
                              <div>
                                <span className="mono smaller">{l.cost_centre_code}</span>{' '}
                                {l.cost_centre_name ?? ''}
                              </div>
                              {l.project_code ? (
                                <div className="smaller muted">
                                  <span className="mono">{l.project_code}</span> {l.project_name ?? ''}
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      {
                        key: 'debit',
                        header: 'Debit',
                        numeric: true,
                        render: (l) => <Money minor={l.debit_minor} blankIfZero />,
                      },
                      {
                        key: 'credit',
                        header: 'Credit',
                        numeric: true,
                        render: (l) => <Money minor={l.credit_minor} blankIfZero />,
                      },
                    ]}
                    footer={
                      <tr className="is-total">
                        <td colSpan={3}>Totals</td>
                        <td className="num">{money(j.total_debit_minor)}</td>
                        <td className="num">{money(j.total_credit_minor)}</td>
                      </tr>
                    }
                  />
                  <p className="smaller muted" style={{ padding: '0 1rem 1rem' }}>
                    Debits equal credits, checked before the posting was accepted and enforced by the database.
                  </p>
                </Card>

                {data.source.document ? (
                  <Card
                    title={`The ${lower(sourceNoun(j.source_table, t))} behind this journal`}
                    subtitle="The sub-ledger document that produced the posting."
                  >
                    <SourceDocument table={j.source_table} document={data.source.document} id={data.source.id} />
                  </Card>
                ) : null}

                {data.documents.length > 0 ? (
                  <Card
                    title="Original documents"
                    subtitle="The end of the drill-down: what was actually received."
                    flush
                    footer={openDocument.error ? <ErrorNotice error={openDocument.error} /> : null}
                  >
                    <DataTable
                      caption="Documents attached to the source record"
                      rows={data.documents}
                      rowKey={(d) => d.id}
                      columns={[
                        { key: 'name', header: 'File', render: (d) => <span className="mono smaller">{d.filename}</span> },
                        { key: 'title', header: 'Title', render: (d) => d.title ?? '—' },
                        {
                          key: 'uploaded',
                          header: 'Uploaded',
                          render: (d) => (
                            <span className="smaller">
                              {date(d.uploaded_at)}
                              {d.uploaded_by_name ? <div className="muted">{d.uploaded_by_name}</div> : null}
                            </span>
                          ),
                        },
                        {
                          key: 'open',
                          header: <span className="visually-hidden">Open</span>,
                          render: (d) => (
                            <button
                              type="button"
                              className="btn btn--sm btn--ghost"
                              disabled={openDocument.busy}
                              onClick={() => {
                                setOpening(d.id);
                                void openDocument.run(d.id, d.filename).finally(() => setOpening(null));
                              }}
                            >
                              {opening === d.id ? 'Opening…' : 'Open'}
                            </button>
                          ),
                        },
                      ]}
                    />
                  </Card>
                ) : null}
              </div>

              <div className="stack">
                <Card title="Provenance">
                  <DefinitionList
                    items={[
                      ['Reference', j.reference ?? '—'],
                      ['Raised by', j.created_by_name ?? '—'],
                      ['Approved by', j.approved_by_name ?? '—'],
                      ['Posted by', j.posted_by_name ?? 'Not posted'],
                      ['Posted on', j.posting_date ? date(j.posting_date) : '—'],
                      ['Source module', statusLabel(j.source_module ?? 'general_ledger')],
                    ]}
                  />
                </Card>

                <Card title="What happened to it" subtitle="From the append-only audit trail.">
                  {data.auditEvents.length === 0 ? (
                    <EmptyState title="No audit events recorded" />
                  ) : (
                    <ul className="timeline">
                      {data.auditEvents.map((e) => (
                        <li key={e.id} className="timeline__item">
                          <p className="timeline__title">{e.summary}</p>
                          <p className="timeline__time">
                            {e.actor_name ?? e.actor_label} · {dateTime(e.occurred_at)}
                          </p>
                          {/*
                            The module, not the event type. The row already reads
                            as a sentence; "ap.invoice.received" underneath it is
                            an internal identifier and tells a reader nothing.
                          */}
                          {e.module ? <p className="smaller muted">{e.module}</p> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            </div>

            <Modal
              open={reversing}
              onClose={() => setReversing(false)}
              title={`Reverse journal ${j.journal_no}`}
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setReversing(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={reverse.busy || !reversalDate || reason.trim().length < 10}
                    onClick={() =>
                      void reverse.run().then((r) => {
                        if (r) {
                          setReversing(false);
                          setReason('');
                          setReversalDate(todayIso());
                          detail.reload();
                        }
                      })
                    }
                  >
                    {reverse.busy ? 'Reversing…' : 'Post the reversal'}
                  </button>
                </>
              }
            >
              <div className="stack">
                <p>
                  A posted journal is never edited or deleted. Reversing it posts an equal and opposite journal that
                  points back at this one, so both remain visible and the trail stays complete.
                </p>
                <TextInput
                  label="Effective date of the reversal"
                  type="date"
                  hint={`The reversal has to fall in a period that is still open for posting. The original is dated ${date(j.effective_date)}.`}
                  value={reversalDate}
                  onChange={(e) => setReversalDate(e.currentTarget.value)}
                  required
                />
                <TextArea
                  label="Why is it being reversed?"
                  hint="At least ten characters. Recorded permanently on both journals."
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  required
                />
                <ErrorNotice error={reverse.error} />
              </div>
            </Modal>
          </>
        );
      }}
    </AsyncPanel>
  );
}

function SourceDocument({
  table,
  document,
  id,
}: {
  table: string | null;
  document: Record<string, unknown>;
  id: string | null;
}) {
  const t = useTerms();
  const header = headerOf(document) ?? {};
  const route = table ? SOURCE_ROUTES[table] : undefined;
  const order = table === 'purchase_order' ? null : orderPointer(header);

  const text = (key: string): string => textField(header, key) ?? '—';
  const when = (key: string): string => {
    const value = textField(header, key);
    return value ? date(value) : '—';
  };
  const amount = (key: string): number | null => {
    const value = header[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
    return null;
  };

  const items: Array<[ReactNode, ReactNode]> = [];
  if (table === 'purchase_invoice') {
    items.push(
      ['Invoice', text('invoice_no')],
      [t.supplier, text('supplier_name')],
      ['Their reference', text('supplier_reference')],
      [t.purchaseOrder, text('order_no')],
      ['Invoice date', when('invoice_date')],
      ['Due date', when('due_date')],
      ['Net', money(amount('net_total_minor'))],
      ['VAT', money(amount('vat_total_minor'))],
      ['Gross', money(amount('gross_total_minor'))],
      ['Entered by', text('entered_by_name')],
      ['Authorised by', text('approved_by_name')],
    );
  } else if (table === 'goods_receipt') {
    const receiptStatus = textField(header, 'status');
    items.push(
      ['Receipt', text('grn_no')],
      [t.purchaseOrder, text('order_no')],
      [t.supplier, text('supplier_name')],
      ['Received', when('receipt_date')],
      ['Delivery note', text('delivery_note_ref')],
      ['Carrier', text('carrier')],
      ['Received by', text('received_by_name')],
      [
        'Status',
        receiptStatus ? <Badge tone={statusTone(receiptStatus)}>{statusLabel(receiptStatus)}</Badge> : '—',
      ],
    );
  } else if (table === 'payment_batch') {
    const method = textField(header, 'payment_method');
    items.push(
      ['Run', text('batch_no')],
      ['Bank account', text('bank_account_name')],
      ['Payment date', when('payment_date')],
      ['Method', method ? PAYMENT_METHODS[method] ?? statusLabel(method) : '—'],
      ['Payments in the run', number(amount('item_count'))],
      ['Total', money(amount('total_minor'))],
      ['Prepared by', text('prepared_by_name')],
      ['Approved by', text('approved_by_name')],
    );
  } else if (table === 'sales_invoice') {
    items.push(
      ['Invoice', text('invoice_no')],
      [t.customer, text('customer_name')],
      ['Invoice date', when('invoice_date')],
      ['Due date', when('due_date')],
      ['Gross', money(amount('gross_total_minor'))],
    );
  } else {
    /*
     * Any sub-ledger the branches above do not name — a depreciation run, an
     * asset, a customer receipt. Show the header's own readable fields, and
     * never a nested object, a database key or an internal identifier: those
     * are what turn a definition list into debug output on a projector.
     */
    for (const [key, value] of Object.entries(header)) {
      if (items.length >= 12) break;
      if (key === 'id' || key.endsWith('_id') || key.endsWith('_by')) continue;
      if (value === null || value === undefined || value === '' || typeof value === 'object') continue;
      if (typeof value === 'string' && UUID_LIKE.test(value)) continue;
      if (key.endsWith('_minor')) {
        items.push([humanise(key.replace(/_minor$/, '')), money(amount(key))]);
      } else if (typeof value === 'boolean') {
        items.push([humanise(key), value ? 'Yes' : 'No']);
      } else if (typeof value === 'number') {
        items.push([humanise(key), number(value)]);
      } else if (typeof value === 'string' && ISO_LIKE.test(value)) {
        items.push([humanise(key), date(value)]);
      } else {
        const shown = String(value);
        items.push([humanise(key), MACHINE_LIKE.test(shown) ? humanise(shown) : shown]);
      }
    }
  }

  return (
    <>
      <DefinitionList items={items} />
      {route && id ? (
        <p style={{ marginTop: '0.75rem' }}>
          <Link className="btn btn--sm" to={`${route}/${id}`}>
            Open the {lower(sourceNoun(table, t))}
          </Link>
        </p>
      ) : null}
      {order ? (
        <p style={{ marginTop: '0.75rem' }}>
          {/*
            A goods receipt has no record page of its own, so the chain carries
            on to the order it was received against — which is where the receipt
            itself is shown.
          */}
          <Link className="btn btn--sm" to={`/orders/${order.id}`}>
            {order.reference ? `Open ${lower(t.purchaseOrder)} ${order.reference}` : `Open the ${lower(t.purchaseOrder)}`}
          </Link>
        </p>
      ) : null}
    </>
  );
}
